import { useState } from 'react';
import type { TeamMember, TeamRole } from '@mockd/shared';
import { InlineEdit, ConfirmDialog, Loading } from '../common';
import { RoleBadge } from './RoleBadge';

type AssignableRole = Exclude<TeamRole, 'owner'>;

const ROLE_OPTIONS: { value: AssignableRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
];

interface TeamMembersListProps {
  members: TeamMember[];
  loading: boolean;
  currentUserId: string | undefined;
  ownerUserId: string;
  canManage: boolean;
  onChangeRole: (userId: string, role: AssignableRole) => Promise<void>;
  onRemove: (userId: string) => Promise<void>;
}

export function TeamMembersList({
  members,
  loading,
  currentUserId,
  ownerUserId,
  canManage,
  onChangeRole,
  onRemove,
}: TeamMembersListProps) {
  const [removing, setRemoving] = useState<TeamMember | null>(null);

  if (loading) {
    return (
      <Loading className="py-6" />
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => {
              const isOwner = member.userId === ownerUserId;
              // Owner and admin can manage everyone but the owner and themselves —
              // self-removal is "leave team," an out-of-scope feature.
              const canManageRow = canManage && !isOwner && member.userId !== currentUserId;
              return (
                <tr key={member.userId}>
                  <td className="text-sm">{member.email}</td>
                  <td>
                    {isOwner ? (
                      <RoleBadge role="owner" />
                    ) : canManageRow ? (
                      <InlineEdit
                        type="select"
                        value={member.role}
                        options={ROLE_OPTIONS}
                        label="Role"
                        onSave={(value) => onChangeRole(member.userId, value as AssignableRole)}
                      />
                    ) : (
                      <RoleBadge role={member.role} />
                    )}
                  </td>
                  <td className="text-sm text-base-content/60">
                    {new Date(member.joinedAt).toLocaleDateString()}
                  </td>
                  <td>
                    {canManageRow && (
                      <button
                        className="btn btn-ghost btn-xs text-error"
                        onClick={() => setRemoving(member)}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={!!removing}
        title="Remove member"
        message={removing ? `Remove ${removing.email} from the team? They'll lose access to team projects.` : ''}
        confirmText="Remove"
        variant="danger"
        onConfirm={async () => {
          if (!removing) return;
          const userId = removing.userId;
          setRemoving(null);
          await onRemove(userId);
        }}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
